/**
 * 도도매 업체 메뉴 설정의 저장 계약.
 *
 * 메뉴 목록 자체는 AppSidebar.buildNavGroups()가 SSOT다. 이 파일은 그 목록을 복제하지 않고,
 * 사이드바 경로(path)를 키로 삼는 설정의 정규화·상속 규칙만 공유한다.
 */

export type ChildStoreMenuSettings = Record<string, boolean>;

const MAX_MENU_SETTINGS = 200;
const MENU_PATH_PATTERN = /^\/[A-Za-z0-9_./:-]+$/;

// 사이드바 메뉴에서 여는 상세/보조 화면. 메뉴 목록 복제가 아니라 "어느 메뉴의 안쪽인가"만
// 적는다. 프론트 라우트 가드와 백엔드 요청 가드가 같은 해석을 쓰도록 공유한다.
const CHILD_STORE_MENU_ROUTE_ALIASES: { prefix: string; menuPath: string }[] = [
  { prefix: "/crm/applications/", menuPath: "/crm/customers" },
  { prefix: "/crm/import/", menuPath: "/crm/import" },
  { prefix: "/crm/settlement-config", menuPath: "/crm/customer-form-settings" },
  { prefix: "/crm/dealer-addons", menuPath: "/crm/customer-form-settings" },
  { prefix: "/inventory/used-devices", menuPath: "/inventory/devices" },
  { prefix: "/policy/review/", menuPath: "/policy/pipeline" },
  { prefix: "/policy/rebate-lookup", menuPath: "/policy/rebate-matrix" },
];

const CRM_APPLICATION_MENU_PATHS = [
  "/crm/customers",
  "/crm/prospects",
  "/crm/activations-complete",
  "/crm/settlements-complete",
  "/crm/archived-applications",
  "/crm/deleted-applications",
  "/crm/settlements",
  "/crm/export",
  "/crm/application-trends",
] as const;

const CRM_SETTINGS_MENU_PATHS = [
  "/crm/customer-form-settings",
  "/crm/pipeline-stages-settings",
] as const;

/** 현재 화면 주소를 저장 키(사이드바 경로)로 해석한다. */
export function childStoreMenuKeyForPath(
  pathnameValue: unknown,
  configuredPaths: readonly string[],
): string | null {
  if (typeof pathnameValue !== "string") return null;
  const pathname = pathnameValue.split(/[?#]/, 1)[0] ?? "";
  if (!MENU_PATH_PATTERN.test(pathname)) return null;
  const alias = CHILD_STORE_MENU_ROUTE_ALIASES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(prefix),
  );
  if (alias) return alias.menuPath;
  const match = [...configuredPaths]
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  // 아직 설정에 한 번도 저장되지 않은 새 메뉴는 자기 경로로 판정되어 false가 된다.
  return match ?? pathname;
}

// 사이드바 배지처럼 **어느 화면에서든** 주기적으로 나가는 조회. 현재 화면과 짝을 맞추면
// 그 메뉴 화면 밖에서는 매번 403 이 나므로(2026-09 상담 배지 1분 폴링이 1천여 건 경보를 냈다),
// 현재 화면 대신 "그 API 가 속한 메뉴가 켜져 있나"만 본다. 프론트는 같은 메뉴 경로로 폴링 여부를 정한다.
export const CHILD_STORE_COUNSEL_MENU_PATH = "/kakao-counsel";
const CHILD_STORE_SIDEBAR_BADGE_APIS: { path: string; menuPath: string }[] = [
  { path: "/api/v2/counsel/pending-drafts/count", menuPath: CHILD_STORE_COUNSEL_MENU_PATH },
];

/** 사이드바 배지용 API면 그 API 가 속한 메뉴 경로를, 아니면 null 을 돌려준다. */
export function childStoreSidebarBadgeMenuPath(apiPath: string): string | null {
  return CHILD_STORE_SIDEBAR_BADGE_APIS.find(({ path }) => path === apiPath)?.menuPath ?? null;
}

/**
 * API 하나가 어느 사이드바 화면을 위해 존재하는지의 서버 가드 계약.
 * 여러 화면이 같은 조회를 쓰면 여러 경로를 반환한다. null은 공용/아직 분류되지 않은 API라
 * 현재 화면 자체의 허용 여부만 검사한다.
 */
export function childStoreMenuPathsForApi(apiPath: string): string[] | null {
  const starts = (prefix: string) => apiPath === prefix || apiPath.startsWith(`${prefix}/`);
  if (starts("/api/v2/library")) return ["/library"];
  if (starts("/api/v2/recurring-tasks")) return ["/recurring-tasks"];
  if (starts("/api/v2/catalog")) return ["/policy/catalog"];
  if (starts("/api/v2/vendors")) return ["/policy/vendors"];

  if (starts("/api/v2/inventory/holdings")) return ["/inventory/holdings"];
  if (starts("/api/v2/inventory")) return ["/inventory/devices"];

  if (starts("/api/v2/counsel/knowledge")) return ["/kakao-counsel/knowledge"];
  if (starts("/api/v2/counsel/learning")) return ["/kakao-counsel/learning"];
  if (starts("/api/v2/counsel")) return ["/kakao-counsel"];

  if (starts("/api/v2/custom-forms") && apiPath.includes("/submissions")) {
    return ["/form-builder/responses"];
  }
  if (starts("/api/v2/custom-forms")) return ["/form-builder"];

  if (starts("/api/v2/store/storefront")) return ["/store/storefront"];
  if (starts("/api/v2/store/child-stores")) {
    return ["/store/staff", "/store/child-stores"];
  }
  if (starts("/api/v2/store/staff")) return ["/store/staff"];

  if (starts("/api/v2/policy/calendar")) return ["/policy/calendar"];
  if (
    starts("/api/v2/policy/board-raw") ||
    starts("/api/v2/policy/raw-matrix-vendor-order") ||
    starts("/api/v2/policy/raw-matrix-plan-order") ||
    starts("/api/v2/policy/raw-matrix-oos-visibility") ||
    starts("/api/v2/policy/raw-excel-hidden-cols") ||
    starts("/api/v2/policy/raw-excel-view") ||
    starts("/api/v2/policy/vendor-verbal-pad")
  ) {
    return ["/policy/rebate-matrix-full"];
  }
  if (
    starts("/api/v2/policy/board/completed") ||
    starts("/api/v2/policy/plan-rebate") ||
    starts("/api/v2/policy/matrix-model-order")
  ) {
    return ["/policy/rebate-matrix", "/policy/quote-sheet"];
  }
  if (starts("/api/v2/policy/quote-sheet")) return ["/policy/quote-sheet"];
  if (
    starts("/api/v2/policy/model-groups") ||
    starts("/api/v2/policy/global-model-groups")
  ) {
    return ["/policy/model-groups"];
  }
  if (
    starts("/api/v2/policy/plan-groups") ||
    starts("/api/v2/policy/vendor-plan-groups")
  ) {
    return ["/policy/plan-groups"];
  }
  if (starts("/api/v2/policy/anomaly-metrics")) return ["/policy/anomalies"];
  if (starts("/api/v2/policy/conflict-review")) return ["/policy/conflict-review"];
  if (starts("/api/v2/policy/subsidy-changes")) return ["/policy/subsidy-changes"];

  if (starts("/api/v2/crm/call-recordings")) return ["/crm/call-recordings"];
  if (starts("/api/v2/crm/legacy-activations")) return ["/crm/legacy-activations"];
  if (starts("/api/v2/crm/import")) return ["/crm/import"];
  if (starts("/api/v2/crm/checkpoints")) return ["/crm/restore"];
  if (
    starts("/api/v2/crm/form-templates") ||
    starts("/api/v2/crm/rendered-forms") ||
    starts("/api/v2/crm/form-template-mappings")
  ) {
    return ["/crm/form-templates"];
  }
  if (
    starts("/api/v2/crm/text-templates") ||
    starts("/api/v2/crm/dealer-variables")
  ) {
    return ["/crm/dealer-templates"];
  }
  if (
    starts("/api/v2/crm/scheduled-messages") ||
    starts("/api/v2/crm/messaging") ||
    starts("/api/v2/crm/message-rules") ||
    starts("/api/v2/crm/sms-automation") ||
    starts("/api/v2/crm/messages-compose")
  ) {
    return [
      "/crm/messages/compose",
      "/crm/messages/today",
      "/crm/messages/calendar",
      "/crm/scheduled-messages",
      "/crm/messages/rules",
      "/crm/messages/history",
    ];
  }
  if (starts("/api/v2/crm/payback")) return ["/crm/payback"];
  // 신청서 안의 검색형 드롭다운은 설정 화면과 같은 매장별 마스터를 쓴다. 신청서를
  // 열 수 있는 도도매 업체라면 그 자리에서 조회·추가·수정·삭제까지 할 수 있어야 한다.
  if (starts("/api/v2/crm/master")) {
    return [...CRM_SETTINGS_MENU_PATHS, ...CRM_APPLICATION_MENU_PATHS];
  }
  if (
    starts("/api/v2/crm/customer-form-config") ||
    starts("/api/v2/crm/retailer-field-config") ||
    starts("/api/v2/crm/pipeline-stages") ||
    starts("/api/v2/crm/settlement-config") ||
    starts("/api/v2/crm/dealer-addons")
  ) {
    return [...CRM_SETTINGS_MENU_PATHS];
  }
  if (starts("/api/v2/crm")) {
    return [...CRM_APPLICATION_MENU_PATHS];
  }
  return null;
}

/** 외부 입력/DB jsonb를 안전한 경로→boolean 맵으로 좁힌다. */
export function normalizeChildStoreMenuSettings(
  value: unknown,
): ChildStoreMenuSettings {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: ChildStoreMenuSettings = {};
  for (const [path, enabled] of Object.entries(value)) {
    if (Object.keys(out).length >= MAX_MENU_SETTINGS) break;
    if (
      typeof enabled === "boolean" &&
      path.length <= 180 &&
      MENU_PATH_PATTERN.test(path)
    ) {
      out[path] = enabled;
    }
  }
  return out;
}

/** 부모 공통값 위에 자식 예외를 얹은 최종 메뉴 설정. 키 없음은 호출부에서 false로 본다. */
export function resolveChildStoreMenuSettings(
  commonValue: unknown,
  overrideValue: unknown,
): ChildStoreMenuSettings {
  return {
    ...normalizeChildStoreMenuSettings(commonValue),
    ...normalizeChildStoreMenuSettings(overrideValue),
  };
}

/** 업체가 저장한 최종 체크 상태 중 공통과 다른 항목만 남긴다. */
export function buildChildStoreMenuOverrides(
  commonValue: unknown,
  effectiveValue: unknown,
): ChildStoreMenuSettings {
  const common = normalizeChildStoreMenuSettings(commonValue);
  const effective = normalizeChildStoreMenuSettings(effectiveValue);
  const out: ChildStoreMenuSettings = {};
  for (const [path, enabled] of Object.entries(effective)) {
    if ((common[path] ?? false) !== enabled) out[path] = enabled;
  }
  return out;
}

/** 지금 공통과 실제로 다른 항목 수. 공통 변경으로 값이 같아진 낡은 예외는 세지 않는다. */
export function countChildStoreMenuDifferences(
  commonValue: unknown,
  overrideValue: unknown,
): number {
  const common = normalizeChildStoreMenuSettings(commonValue);
  const overrides = normalizeChildStoreMenuSettings(overrideValue);
  return Object.entries(overrides).filter(
    ([path, enabled]) => (common[path] ?? false) !== enabled,
  ).length;
}

export function enabledChildStoreMenuPaths(
  commonValue: unknown,
  overrideValue: unknown,
): string[] {
  return Object.entries(resolveChildStoreMenuSettings(commonValue, overrideValue))
    .filter(([, enabled]) => enabled)
    .map(([path]) => path)
    .sort();
}
